import { Box, CircularProgress } from '@mui/material';
import React, { RefObject } from 'react';
import ContentSection from '../common/ContentSection';
import ContentWrapper from '../common/ContentWrapper';
import { Palette } from 'Palette';
import { StyledTypography } from '../styles/styles';
import ContentDiffCommentOverlays from './ContentDiffCommentOverlays';
import { OverlayEntry } from './contentDiffComparisonTypes';
import { getDiffDisplayBoxSx } from './contentDiffComparisonUtils';

interface ContentDiffDisplayPanelProps {
  isUserReady: boolean;
  isCalculating: boolean;
  error: string | null;
  diffHtml: string;
  diffContainerRef: RefObject<HTMLDivElement | null>;
  overlayPositions: OverlayEntry[];
  tone: keyof typeof Palette;
  containerSx?: Record<string, unknown>;
  sectionData: Array<{
    sectionId: string;
    hasChanges: boolean;
    isAccepted: boolean;
  }>;
  comments: Array<{
    conversationId: string;
    sectionId: string;
    comments: {
      message: string;
      submittedBy: { id: string; name: string };
      createdAt: string;
    }[];
  }>;
  currentUser?: string;
  onReply?: (conversationId: string, message: string) => void;
  onAcceptChanges?: (sectionId: string) => void;
  renderActions?: (sectionId: string, hasChanges: boolean, isAccepted: boolean) => React.ReactNode;
}

const ContentDiffDisplayPanel: React.FC<ContentDiffDisplayPanelProps> = ({
  isUserReady,
  isCalculating,
  error,
  diffHtml,
  diffContainerRef,
  overlayPositions,
  tone,
  containerSx,
  sectionData,
  currentUser,
  onReply,
  onAcceptChanges,
  renderActions,
}) => {
  if (!isUserReady) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 4,
        }}
      >
        <CircularProgress sx={{ mb: 2 }} />
        <StyledTypography variant="body2" color="text.secondary">
          Loading user…
        </StyledTypography>
      </Box>
    );
  }

  if (isCalculating) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          py: 4,
        }}
      >
        <CircularProgress sx={{ mb: 2 }} />
        <StyledTypography variant="h6" tone={tone}>
          Calculating diff...
        </StyledTypography>
        <StyledTypography variant="body2" color="text.secondary">
          Rendering and loading all content for comparison
        </StyledTypography>
      </Box>
    );
  }

  if (error) {
    return (
      <>
        <StyledTypography color="error" variant="h6">
          Error calculating diff
        </StyledTypography>
        <StyledTypography color="text.secondary" sx={{ mt: 1 }}>
          {error}
        </StyledTypography>
      </>
    );
  }

  if (!diffHtml.trim()) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <StyledTypography color="text.secondary">
          No differences found between the selected versions.
        </StyledTypography>
      </Box>
    );
  }

  return (
    <ContentWrapper
      sx={{
        '& .content-management-container': {
          px: 0,
          mx: 0,
        },
      }}
    >
      <ContentSection>
        <Box
          key={`diff-${diffHtml.length}`}
          ref={diffContainerRef}
          sx={getDiffDisplayBoxSx(containerSx)}
          dangerouslySetInnerHTML={{ __html: diffHtml }}
        />

        <ContentDiffCommentOverlays
          overlayPositions={overlayPositions}
          sectionData={sectionData}
          tone={tone}
          currentUser={currentUser}
          onReply={onReply}
          onAcceptChanges={onAcceptChanges}
        />

        {renderActions &&
          sectionData.map(({ sectionId, hasChanges, isAccepted }) => (
            <Box
              key={`actions-${sectionId}`}
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                zIndex: 10,
              }}
            >
              {renderActions(sectionId, hasChanges, isAccepted)}
            </Box>
          ))}
      </ContentSection>
    </ContentWrapper>
  );
};

export default ContentDiffDisplayPanel;
